import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import QuickCapturePanel from "./QuickCapturePanel";
import useLocalStorage from "./MediaClips/useLocalStorage";
import useYouTubePlayer from "./MediaClips/useYouTubePlayer";
import ChannelTable from "./MediaClips/ChannelTable";
import WatchOnYouTubeLink from "./YouTubeConsent/WatchOnYouTubeLink";
import YouTubeConsentPrompt from "./YouTubeConsent/YouTubeConsentPrompt";
import {
  CAPTURE_KEYS, isTyping, KEYS, matches, NEXT_KEYS, PREVIOUS_KEYS, REWIND_KEYS, REWIND_SECONDS,
  TOGGLE_KEYS
} from "./MediaClips/hotkeys";

// "In the Wild" - a levelled library of short authentic videos in the target language.
// See docs/media_clips.md.
//
// POC: the whole library arrives as props from a JSON seed file, so filtering and sorting
// happen here rather than as requests. At a few hundred clips that's strictly better than
// round trips; past a couple of thousand it stops being, and that's the point at which
// this moves to the database.
//
// The layout puts the video and Quick Capture side by side because that IS the product:
// you hear a word, you type it, it becomes a cloze. Everything else on the page is
// navigation around that one interaction.
//
// Captions are YouTube's own. We never render transcript text ourselves - the transcript
// is used to classify a clip and then discarded, and the embedded player's terms forbid
// overlaying it anyway.

// Filtering on the stored level rather than on A1-A2 / B1-B2 bands. Bands were the more
// cautious choice — a single level off 30-180 seconds of speech is a noisy judgement — but
// they collapse the distinction a learner most wants, between B1 and B2, and at a hundred
// clips per language there are enough in each level for the finer filter to be usable.
// The honest claim is still about aggregates: a level here means "clips like this", not a
// guarantee about any one of them.
const LEVELS = ["a1", "a2", "b1", "b2", "c1", "c2"];

const SORTS = [
  { key: "level", label: "Level" },
  { key: "shortest", label: "Shortest" },
  { key: "slowest", label: "Slowest speech" },
  { key: "popular", label: "Most viewed" },
];

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins ? `${mins}m${String(secs).padStart(2, "0")}s` : `${secs}s`;
};

const formatViews = (views) => {
  if (!views) return null;
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M views`;
  if (views >= 1000) return `${Math.round(views / 1000)}K views`;
  return `${views} views`;
};

// Tags are stored lowercase so "Cooking" and "cooking" can't split one filter into two, and
// capitalised only for display. First letter only - "Futur Simple" is wrong where "Futur
// simple" is right, so title case would be worse than none.
const sentenceCase = (text) =>
  typeof text === "string" && text.length ? text[0].toUpperCase() + text.slice(1) : text;

// Used only when the IFrame API is unavailable. Same caption parameters the API gets, since
// those are playerVars either way — the fallback loses our transport controls, not captions.
const embedUrl = (clip) => {
  const params = new URLSearchParams({ cc_load_policy: "1", playsinline: "1", rel: "0" });

  if (clip.caption_language) {
    params.set("cc_lang_pref", clip.caption_language);
    params.set("hl", clip.caption_language);
  }

  return `https://www.youtube.com/embed/${clip.external_id}?${params.toString()}`;
};

const shortsUrl = (clip) => `https://www.youtube.com/shorts/${clip.external_id}`;

// The line under the title. Built from our own derived data rather than the video's
// description, which is where the value of the library actually is.
const descriptorParts = (clip) =>
  [
    clip.cefr_level && clip.cefr_level.toUpperCase(),
    sentenceCase((clip.themes || [])[0]),
    formatDuration(clip.duration_seconds),
    clip.words_per_minute && `${Math.round(clip.words_per_minute)} wpm`,
  ].filter(Boolean);

const MediaClips = ({
  channels, clips, isSignedIn, quickCapture, signInUrl, signUpUrl, targetLanguageName
}) => {
  const [level, setLevel] = useState(null);
  const [category, setCategory] = useState(null);
  const [tag, setTag] = useState(null);
  const [sort, setSort] = useState("level");
  const [selected, setSelected] = useState(clips[0] || null);
  // Open by default, and remembered. Collapsed hides the one thing that makes this more
  // than a list of YouTube links — and the counts alongside each option ("B1 650") are
  // themselves the argument that the library is real, which nobody sees behind a chevron.
  //
  // Persisted so someone who closes them keeps them closed; the default only decides what
  // a first-time visitor sees.
  const [filtersOpen, setFiltersOpen] = useLocalStorage("filtersOpen", true);

  // Persisted because it's a working preference, not a session one - someone who finds
  // auto-pause intrusive shouldn't have to turn it off on every visit.
  const [pauseWhileTyping, setPauseWhileTyping] = useLocalStorage("pauseWhileTyping", true);

  // Channels is the default. The key is deliberately not "view": a stored preference from
  // when Player was the default would otherwise win, and nobody who'd already visited would
  // ever see the change.
  const [view, setView] = useLocalStorage("view2", "table");

  const { acceptCookies, consented, containerRef, failed, pause, play, playing, seek, toggle } = useYouTubePlayer({
    captionLanguage: selected && selected.caption_language,
    videoId: selected && selected.external_id,
  });

  const captureRef = useRef(null);
  const playerRef = useRef(null);

  // Focus pauses; nothing auto-resumes. Resuming on blur guessed wrong constantly - after
  // submitting an entry you often want to read what came back, and a video restarting
  // under you is worse than one that waited. So the page says how to start it again and
  // otherwise leaves it alone.
  const [awaitingResume, setAwaitingResume] = useState(false);
  const [resumeHintDismissed, setResumeHintDismissed] = useLocalStorage("resumeHintSeen", false);

  const resume = useCallback(() => {
    setAwaitingResume(false);
    play();
  }, [play]);

  const handleCaptureFocus = useCallback(() => {
    if (!pauseWhileTyping) return;
    if (pause()) setAwaitingResume(true);
  }, [pause, pauseWhileTyping]);

  // However playback restarted - our key, our button, or YouTube's own controls - the
  // prompt to restart it has served its purpose.
  useEffect(() => {
    if (playing) setAwaitingResume(false);
  }, [playing]);

  const focusCapture = useCallback(() => {
    const field = captureRef.current && captureRef.current.querySelector("textarea, input");

    if (field) field.focus();
  }, []);

  // Counts come from the whole library rather than the current filter, so a button
  // showing "12" doesn't drop to "0" the moment you narrow by something else and leave
  // the row looking broken.
  const [categories, tags, levels] = useMemo(() => {
    const tally = (values, counts) => {
      values.filter(Boolean).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
      return counts;
    };

    const byCount = (counts) => [...counts.entries()].sort((a, b) => b[1] - a[1]);

    const categoryCounts = new Map();
    const tagCounts = new Map();
    const levelCounts = new Map();

    clips.forEach((clip) => {
      tally([clip.ai_category], categoryCounts);
      tally(clip.grammar_tags || [], tagCounts);
      tally([clip.cefr_level], levelCounts);
    });

    // Levels keep CEFR order rather than sorting by count, and empty ones are dropped —
    // a dead A1 button on a library with no A1 clips is just something to click and
    // wonder about.
    const presentLevels = LEVELS
      .filter((name) => levelCounts.has(name))
      .map((name) => [name, levelCounts.get(name)]);

    return [byCount(categoryCounts), byCount(tagCounts).slice(0, 12), presentLevels];
  }, [clips]);

  const visible = useMemo(() => {
    const filtered = clips.filter((clip) => {
      if (level && clip.cefr_level !== level) return false;
      if (category && clip.ai_category !== category) return false;
      if (tag && !(clip.grammar_tags || []).includes(tag)) return false;
      return true;
    });

    const sorters = {
      level: (a, b) => LEVELS.indexOf(a.cefr_level) - LEVELS.indexOf(b.cefr_level),
      popular: (a, b) => (b.view_count || 0) - (a.view_count || 0),
      shortest: (a, b) => (a.duration_seconds || 0) - (b.duration_seconds || 0),
      slowest: (a, b) => (a.words_per_minute || 0) - (b.words_per_minute || 0),
    };

    return [...filtered].sort(sorters[sort]);
  }, [clips, level, category, tag, sort]);

  const step = useCallback(
    (offset) => {
      if (!selected) return;

      const index = visible.findIndex((clip) => clip.external_id === selected.external_id);
      const next = visible[index + offset];

      if (next) setSelected(next);
    },
    [selected, visible]
  );

  // Playing from the channel table swaps the whole page under you - without this you're
  // left looking at the row you clicked while the video plays somewhere above the fold.
  const playFromTable = useCallback((clip) => {
    setSelected(clip);
    setView("player");

    // After the view actually swaps, not before.
    window.requestAnimationFrame(() => {
      if (playerRef.current) playerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [setView]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      // Escape is the one key that works from inside the capture box, because it's how you
      // say "done adding words" - every other shortcut is a letter someone might be typing.
      if (isTyping(event.target)) {
        if (!matches(event, ["Escape"])) return;

        event.target.blur();
        resume();
        return;
      }

      if (matches(event, TOGGLE_KEYS)) {
        // Space scrolls the page by default, which is not what pressing it over a video
        // should do.
        event.preventDefault();
        toggle();
      } else if (matches(event, REWIND_KEYS)) {
        seek(-REWIND_SECONDS);
      } else if (matches(event, CAPTURE_KEYS)) {
        event.preventDefault();
        focusCapture();
      } else if (matches(event, NEXT_KEYS)) {
        step(1);
      } else if (matches(event, PREVIOUS_KEYS)) {
        step(-1);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [focusCapture, resume, seek, step, toggle]);

  const clearFilters = () => {
    setLevel(null);
    setCategory(null);
    setTag(null);
  };

  const activeFilters = [level && level.toUpperCase(), category, tag].filter(Boolean);

  if (!clips.length) {
    return (
      <div className="media-clips-empty">
        {/* No heading — the page title in the view already says where you are. */}
        <p>
          No {targetLanguageName} clips yet. Generate them with{" "}
          <code>rake media_clips:poc</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="media-clips">
      {/* Collapsed by default: filters are browse-time, not watch-time. The summary stays
          visible because with a short list you're navigating a narrow window, so what's
          currently applied matters more, not less. */}
      <div className="media-clips-filters">
        <button
          className="media-clips-filters-toggle"
          onClick={() => setFiltersOpen(!filtersOpen)}
          type="button"
        >
          {activeFilters.length ? activeFilters.join(" · ") : "All clips"}
          <span className="media-clips-count">
            {visible.length} of {clips.length}
          </span>
          <span className="media-clips-chevron">{filtersOpen ? "▴" : "▾"}</span>
        </button>

        {activeFilters.length > 0 && (
          <button className="media-clips-clear" onClick={clearFilters} type="button">
            Clear
          </button>
        )}

        <div className="media-clips-views">
          {["player", "table"].map((name) => (
            <button
              className={view === name ? "is-active" : ""}
              key={name}
              onClick={() => setView(name)}
              type="button"
            >
              {name === "player" ? "Player" : "Channels"}
            </button>
          ))}
        </div>

        {filtersOpen && (
          <div className="media-clips-filter-panel">
            <div className="media-clips-filter-group">
              <label>Level</label>
              {levels.map(([name, count]) => (
                <button
                  className={level === name ? "is-active" : ""}
                  key={name}
                  onClick={() => setLevel(level === name ? null : name)}
                  type="button"
                >
                  {name.toUpperCase()} <span className="media-clips-count">{count}</span>
                </button>
              ))}
            </div>

            {categories.length > 1 && (
              <div className="media-clips-filter-group">
                <label>Topic</label>
                {categories.map(([name, count]) => (
                  <button
                    className={category === name ? "is-active" : ""}
                    key={name}
                    onClick={() => setCategory(category === name ? null : name)}
                    type="button"
                  >
                    {sentenceCase(name)} <span className="media-clips-count">{count}</span>
                  </button>
                ))}
              </div>
            )}

            {tags.length > 0 && (
              <div className="media-clips-filter-group">
                <label>Grammar</label>
                {tags.map(([name, count]) => (
                  <button
                    className={tag === name ? "is-active" : ""}
                    key={name}
                    onClick={() => setTag(tag === name ? null : name)}
                    type="button"
                  >
                    {sentenceCase(name)} <span className="media-clips-count">{count}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="media-clips-filter-group">
              <label htmlFor="media-clips-sort">Sort</label>
              <select
                id="media-clips-sort"
                onChange={(e) => setSort(e.target.value)}
                value={sort}
              >
                {SORTS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {view === "table" && (
        <ChannelTable
          channels={channels}
          clips={visible}
          formatDuration={formatDuration}
          onPlay={playFromTable}
          selectedId={selected && selected.external_id}
          sentenceCase={sentenceCase}
        />
      )}

      {/* Hidden rather than unmounted: tearing down the player on every view switch would
          destroy and rebuild the iframe, losing playback position and costing a reload.
          The SCSS carries a [hidden] rule because display:flex would otherwise win. */}
      <div className="media-clips-body" hidden={view === "table"}>
        <div className="media-clips-main">
          <div className="media-clips-player" ref={playerRef}>
            <div className="media-clips-frame">
              {/* Nothing YouTube-shaped renders before consent. The plain-iframe fallback
                  below would be blocked too — the consent manager defers embeds from
                  cookie-setting domains, not just scripts — so an unconsented viewer needs
                  a prompt, not a different flavour of blocked player. */}
              {!consented ? (
                <YouTubeConsentPrompt onAccept={acceptCookies} />
              ) : failed && selected ? (
                <iframe
                  allow="accelerometer; encrypted-media; picture-in-picture"
                  allowFullScreen
                  key={selected.external_id}
                  src={embedUrl(selected)}
                  title={selected.title}
                />
              ) : (
                // The API replaces this node with its iframe.
                <div ref={containerRef} />
              )}
            </div>

            {/* Our own transport, so there's no reason to click into the player. Once the
                iframe takes focus, keystrokes belong to YouTube and our shortcuts go
                quiet - these keep focus in the document where they still work. */}
            {/* Prev and Next still work without the API — they only change which clip is
                selected. Play/pause and rewind don't, so they're not offered. */}
            <div className="media-clips-controls">
              <button onClick={() => step(-1)} title="Previous clip (p)" type="button">
                ‹ Prev
              </button>
              {!failed && (
                <>
                  <button
                    onClick={() => seek(-REWIND_SECONDS)}
                    title={`Back ${REWIND_SECONDS} seconds (j)`}
                    type="button"
                  >
                    ↺ {REWIND_SECONDS}s
                  </button>
                  <button
                    className="media-clips-play"
                    onClick={toggle}
                    title="Play or pause (space)"
                    type="button"
                  >
                    {playing ? "❙❙ Pause" : "▶ Play"}
                  </button>
                </>
              )}
              <button onClick={() => step(1)} title="Next clip (n)" type="button">
                Next ›
              </button>
            </div>

            {/* The fallback embed can be blocked too (DuckDuckGo blocks YouTube outright), and
                then the frame stays black - so there's always a way to the video itself. */}
            {failed && selected && (
              <WatchOnYouTubeLink href={shortsUrl(selected)} />
            )}

            {selected && (
              <>
                <h2 className="media-clips-now-playing">{selected.title}</h2>
                <p className="media-clips-descriptor">{descriptorParts(selected).join(" · ")}</p>

                {(selected.grammar_tags || []).length > 0 && (
                  <p className="media-clips-descriptor">
                    {selected.grammar_tags.map(sentenceCase).join(", ")}
                  </p>
                )}
              </>
            )}
          </div>

          <h3 className="media-clips-pane-heading">
            Up next <span className="media-clips-key">n</span>
          </h3>

          <ol className="media-clips-list media-clips-list-scroll">
            {visible.map((clip) => (
              <li key={clip.external_id}>
                <button
                  className={
                    selected && selected.external_id === clip.external_id ? "is-active" : ""
                  }
                  onClick={() => setSelected(clip)}
                  type="button"
                >
                  <span className="media-clips-title">{clip.title}</span>
                  <span className="media-clips-descriptor">
                    {descriptorParts(clip).join(" · ")}
                    {formatViews(clip.view_count) ? ` · ${formatViews(clip.view_count)}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </div>

        <div className="media-clips-side">
          {/* onFocus on the capture box specifically, not the whole column: it bubbles, so
              one handler covers every field Quick Capture renders — but scoped any wider
              and ticking a settings checkbox would pause the video. */}
          <div className="media-clips-capture" onFocus={handleCaptureFocus} ref={captureRef}>
            <h3 className="media-clips-pane-heading">
              Quick Capture <span className="media-clips-key">q</span>
            </h3>

            {/* Shown only while we're the reason the video stopped, and dismissable for
                good — it's a one-time thing to learn, and a banner that never goes away
                stops being read after the second time. */}
            {awaitingResume && !resumeHintDismissed && (
              <p className="media-clips-hint">
                Video paused. Press <span className="media-clips-key">esc</span> to keep
                playing.
                <button onClick={() => setResumeHintDismissed(true)} type="button">
                  Got it
                </button>
              </p>
            )}

            {/* Same panel the modal shows, so non-Pro visitors get the promo and demo
                inline rather than a button that opens a dialog over the video. */}
            {quickCapture && (
              <QuickCapturePanel
                {...quickCapture}
                isSignedIn={isSignedIn}
                signInUrl={signInUrl}
                signUpUrl={signUpUrl}
              />
            )}
          </div>

          <div className="media-clips-settings">
            <h3 className="media-clips-pane-heading">Settings</h3>

            {failed && (
              <p className="media-clips-note">
                <strong>Playback controls unavailable.</strong> Something is blocking
                YouTube&apos;s player script — usually an ad blocker or privacy extension.
                The video and captions still work; pause-while-typing and the keyboard
                shortcuts don&apos;t. Allowing <code>youtube.com</code> on this page brings
                them back.
              </p>
            )}

            <label className="media-clips-setting">
              <input
                checked={pauseWhileTyping}
                disabled={failed}
                onChange={(e) => setPauseWhileTyping(e.target.checked)}
                type="checkbox"
              />
              <span>Pause while typing</span>
            </label>

            <h3 className="media-clips-pane-heading media-clips-keys-heading">Shortcuts</h3>

            {/* Worth saying out loud: once you click the player, the keyboard belongs to
              YouTube's iframe and ours goes quiet. Nothing we can intercept — it's
              cross-origin — so the honest fix is telling people, plus the buttons under
              the video that mean you never have to click into it. */}
            <p className="media-clips-note">
              Focusing Quick Capture pauses the video, and it won&apos;t start again on its
              own — press <span className="media-clips-key">esc</span> when you&apos;re ready
              to carry on.
            </p>

            <p className="media-clips-note">
              Shortcuts need focus outside the video. Click the player and the keyboard goes
              to YouTube instead — where ← and → seek rather than change clip. Click anywhere
              on the page to get them back.
            </p>

            <dl className="media-clips-keys">
              {KEYS.map(({ hint, label }) => (
                <React.Fragment key={hint}>
                  <dt>
                    <span className="media-clips-key">{hint}</span>
                  </dt>
                  <dd>{label}</dd>
                </React.Fragment>
              ))}
            </dl>

            {/* We already pass cc_lang_pref and hl for every clip, and they're correct. But
                YouTube keeps the viewer's caption choice against their own account, and once
                auto-translate is on it overrides what an embed asks for — so someone who
                studies two languages here sees the first one's captions on the second. The
                only override is the undocumented captions module, which the developer
                policies prohibit. Nothing to fix in code; the switch is theirs. */}
            <p className="media-clips-note media-clips-note-last">
              <strong>Captions in the wrong language?</strong> YouTube remembers your last
              caption choice across videos and it overrides ours. Fix it in the player: gear
              icon → Subtitles → pick {targetLanguageName}, and turn off auto-translate if
              it&apos;s on.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

MediaClips.propTypes = {
  channels: PropTypes.object,
  clips: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSignedIn: PropTypes.bool,
  quickCapture: PropTypes.object,
  signInUrl: PropTypes.string,
  signUpUrl: PropTypes.string,
  targetLanguageName: PropTypes.string.isRequired,
};

MediaClips.defaultProps = {
  channels: {},
  isSignedIn: false,
  quickCapture: null,
  signInUrl: null,
  signUpUrl: null,
};

export default MediaClips;
